<template>
  <CouponBadge v-if="showCouponBadge" class="coupon-badge" />
  <div class="plan-info-container">
    <h3 class="title" data-test="plan-summary-title">{{ title }}</h3>
    <div class="plan-info">
      <p class="price-details" data-test="plan-summary-price">
        <span v-if="strikethroughPricing" class="strikethrough-price"> {{ strikethroughPricing }} </span>&nbsp;<span class="price">
          {{ pricing }}
        </span>
        {{ trialInfo }}
      </p>
      <p v-if="secondarySubheading && !isForeverCoupon" class="secondary-subheading">{{ secondarySubheading }}</p>
      <p class="subheading">{{ subheading }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import CouponBadge from '@/components/shared/CouponBadge.vue'
import { useCouponStore } from '@/store/CouponStore'

const couponStore = useCouponStore()
const { isForeverCoupon } = storeToRefs(couponStore)

defineProps({
  showCouponBadge: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  pricing: {
    type: String,
    required: true
  },
  strikethroughPricing: {
    type: String,
    required: false
  },
  subheading: {
    type: String,
    required: true
  },
  secondarySubheading: {
    type: String,
    required: false
  },
  trialInfo: {
    type: String,
    required: false
  }
})
</script>

<style lang="scss" scoped>
.coupon-badge {
  color: var(--color-white);
  margin: 0 auto;
  width: fit-content;
  position: relative;
  top: 15px;
  z-index: 1;
}

.plan-info-container {
  border-radius: $border-radius-lg;
  border: 1px solid $gray60;
  margin-bottom: $spacer-6;

  .title {
    color: var(--color-plan-card-price); // Product name: #F1E7D1
    font-family: $font-family-pilat-bold;
    padding: $spacer-4 $spacer-3 $spacer-3 $spacer-3;
    background: var(--color-plan-card-header-bg);
    width: 100%;
    text-align: center;
    border-top-left-radius: $border-radius-lg;
    border-top-right-radius: $border-radius-lg;
    font-size: 28px; // Per AC
    line-height: 32px;
    margin-bottom: 0;
  }

  .plan-info {
    text-align: center;
    font-family: $font-family-pilat-regular;
    padding: $spacer-4 $spacer-4 $spacer-5 $spacer-4;
    background: var(--color-plan-card-body-bg);
    border-bottom-left-radius: $border-radius-lg;
    border-bottom-right-radius: $border-radius-lg;
  }

  .price-details {
    font-family: $font-family-pilat-bold;
    font-size: 20px;
    color: var(--color-plan-card-price); // Non-promo price: #F1E7D1
    line-height: 28px;
    margin-bottom: 10px;

    .price {
      color: var(--color-plan-card-price-promo); // Promo price: #E5FF00
    }

    .strikethrough-price {
      text-decoration: line-through;
    }
  }

  .subheading,
  .secondary-subheading {
    font-size: 16px;
    color: var(--color-plan-card-text-secondary); // Trial reminder/cancel: #F1E7D199
    margin-bottom: 4px;
    line-height: 24px;
  }

  .secondary-subheading {
    color: var(--color-plan-card-price); // Post-discount price: #F1E7D1
  }
}
</style>
